import React, { useState } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';

import { Box, colors, breakpoints, Icon } from '../../../design-system';
import { useMediaQueries } from '../../common/hooks/useMediaQuery';
import { Product } from '../types';
import { CmsProduct } from '../../cms/types';
import { CsrTable } from './CsrTable';

const StyledButton = styled.button<{ isOpen?: boolean }>`
  text-align: left;
  color: ${colors.BLACK};
  background-color: ${colors.BACKGROUND};
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 16px;
  border: none;
  border-bottom: ${({ isOpen }) => (isOpen ? 'none' : `solid 1px ${colors.LIGHT}`)};
  @media (hover: hover) {
    :hover {
      cursor: pointer;
    }
  }
`;

type Props = {
  product: Product;
  cmsProduct: CmsProduct;
};

export const CsrBlock = ({ product, cmsProduct }: Props) => {
  const { isMobile, isTablet } = useMediaQueries();

  const [firstItemIsOpen, setFirstItemIsOpen] = useState(false);
  const [secondItemIsOpen, setSecondItemIsOpen] = useState(false);
  const [thirdItemIsOpen, setThirdItemIsOpen] = useState(false);
  const [fourthItemIsOpen, setFourthItemIsOpen] = useState(false);
  const [fifthItemIsOpen, setFifthItemIsOpen] = useState(false);
  const [sixthItemIsOpen, setSixthItemIsOpen] = useState(false);
  const {
    contentRecycledMaterials,
    recyclability,
    rejectsMicrofibers,
    toxicity,
    traceability,
    productRef,
    glimpact: glimpactSeason,
  } = product ?? {};

  if (
    !contentRecycledMaterials &&
    !recyclability &&
    !rejectsMicrofibers &&
    !toxicity &&
    !traceability &&
    !glimpactSeason
  ) {
    return null;
  }

  const {
    csr_block_title,
    title_rejects_microfibers,
    product_rejects_microfibers_is_true,
    product_rejects_microfibers_is_false,
    title_recycled_materials,
    recycled_material_is_null,
    recycled_materials_intermediate_value,
    recycled_materials_is_full,
    title_recyclability,
    product_recyclability_is_true,
    product_recyclability_is_false,
    title_toxicity,
    product_toxicity_is_true,
    product_toxicity_is_false,
    title_traceability,
  } = cmsProduct ?? {};

  const displayPercentage = recycled_materials_intermediate_value?.replace(
    '$percentage',
    contentRecycledMaterials?.toString()
  );

  return (
    <div
      id="csr-block"
      css={css`
        background-color: ${colors.BACKGROUND};
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 32px 0;
        border-radius: 8px;
        margin: 16px 0;

        @media (min-width: ${breakpoints.S}px) {
          margin: 0 16px;
        }

        @media (min-width: ${breakpoints.M}px) {
          margin: 56px 0 0 0;
        }
      `}
    >
      <Box
        id="csr-block-title"
        textAlign="center"
        fontSize="20px"
        lineHeight="22px"
        fontWeight="700"
        width={isMobile ? '90%' : isTablet ? '75%' : '800px'}
        mb="24px"
      >
        {csr_block_title && csr_block_title}
      </Box>
      <div
        id="csr-block-items"
        css={css`
          width: 100%;
          display: flex;
          flex-direction: column;

          @media (min-width: ${breakpoints.S}px) {
            width: 90%;
          }

          @media (min-width: ${breakpoints.M}px) {
            width: 944px;
          }

          @media (min-width: ${breakpoints.L}px) {
            width: 1024px;
          }
        `}
      >
        {((contentRecycledMaterials === 0 &&
          recycled_material_is_null &&
          recycled_material_is_null !== '') ||
          (contentRecycledMaterials === 100 &&
            recycled_materials_is_full &&
            recycled_materials_is_full !== '') ||
          (contentRecycledMaterials > 0 &&
            contentRecycledMaterials < 100 &&
            recycled_materials_intermediate_value &&
            recycled_materials_intermediate_value !== '')) && (
          <StyledButton
            type="button"
            data-testid={`csr-block-item-0-btn-to-${firstItemIsOpen ? 'close' : 'open'}`}
            aria-expanded={firstItemIsOpen}
            aria-label={`Bouton pour ${firstItemIsOpen ? 'fermer' : 'ouvrir'} le block ${title_recycled_materials}`}
            id="csr-block-item-1"
            onClick={() => setFirstItemIsOpen(!firstItemIsOpen)}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="calc(100% - 32px)"
              fontSize="16px"
              lineHeight="110%"
              fontWeight="700"
            >
              <Box fontSize="16px" lineHeight="110%" fontWeight="700">
                {title_recycled_materials && title_recycled_materials}
              </Box>
              {firstItemIsOpen && (
                <Box mt="16px" fontSize="14px" lineHeight="18px">
                  {contentRecycledMaterials > 0 && contentRecycledMaterials < 100
                    ? displayPercentage
                    : contentRecycledMaterials === 100
                      ? recycled_materials_is_full
                      : recycled_material_is_null}
                </Box>
              )}
            </Box>
            <Box width="32px" display="flex" justifyContent="flex-end">
              <Icon name={firstItemIsOpen ? 'minus' : 'plus'} size={isMobile ? 12 : 16} />
            </Box>
          </StyledButton>
        )}

        {Boolean(traceability) && typeof traceability === 'string' && (
          <StyledButton
            type="button"
            data-testid={`csr-block-item-1-btn-to-${secondItemIsOpen ? 'close' : 'open'}`}
            aria-expanded={secondItemIsOpen}
            aria-label={`Bouton pour ${secondItemIsOpen ? 'fermer' : 'ouvrir'} le block ${title_traceability}`}
            id="csr-block-item-2"
            onClick={() => setSecondItemIsOpen(!secondItemIsOpen)}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="calc(100% - 32px)"
              fontSize="16px"
              lineHeight="110%"
              fontWeight="700"
            >
              <Box fontSize="16px" lineHeight="110%" fontWeight="700">
                {title_traceability && title_traceability}
              </Box>
            </Box>
            <Box width="32px" display="flex" justifyContent="flex-end">
              <Icon name={secondItemIsOpen ? 'minus' : 'plus'} size={isMobile ? 12 : 16} />
            </Box>
          </StyledButton>
        )}

        {secondItemIsOpen && isMobile && traceability && (
          <Box width="calc(100vw - 32px)">
            <CsrTable cmsProduct={cmsProduct} traceability={traceability} />
          </Box>
        )}
        {secondItemIsOpen && !isMobile && traceability && (
          <Box width="100%">
            <CsrTable cmsProduct={cmsProduct} traceability={traceability} />
          </Box>
        )}
        {secondItemIsOpen && <Box height="1px" borderTop="solid 1px #E6E6E6" mt="24px" />}
        {((recyclability === true &&
          product_recyclability_is_true &&
          product_recyclability_is_true !== '') ||
          (recyclability === false &&
            product_recyclability_is_false &&
            product_recyclability_is_false !== '')) && (
          <StyledButton
            type="button"
            data-testid={`csr-block-item-2-btn-to-${thirdItemIsOpen ? 'close' : 'open'}`}
            aria-expanded={thirdItemIsOpen}
            aria-label={`Bouton pour ${thirdItemIsOpen ? 'fermer' : 'ouvrir'} le block ${title_recyclability}`}
            id="csr-block-item-3"
            onClick={() => setThirdItemIsOpen(!thirdItemIsOpen)}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="calc(100% - 32px)"
              fontSize="16px"
              lineHeight="110%"
              fontWeight="700"
            >
              <Box fontSize="16px" lineHeight="110%" fontWeight="700">
                {title_recyclability && title_recyclability}
              </Box>
              {thirdItemIsOpen && (
                <Box mt="16px" fontSize="14px" lineHeight="18px">
                  {recyclability ? product_recyclability_is_true : product_recyclability_is_false}
                </Box>
              )}
            </Box>
            <Box width="32px" display="flex" justifyContent="flex-end">
              <Icon name={thirdItemIsOpen ? 'minus' : 'plus'} size={isMobile ? 12 : 16} />
            </Box>
          </StyledButton>
        )}

        {((rejectsMicrofibers === true &&
          product_rejects_microfibers_is_true &&
          product_rejects_microfibers_is_true !== '') ||
          (rejectsMicrofibers === false &&
            product_rejects_microfibers_is_false &&
            product_rejects_microfibers_is_false !== '')) && (
          <StyledButton
            type="button"
            data-testid={`csr-block-item-3-btn-to-${fourthItemIsOpen ? 'close' : 'open'}`}
            aria-expanded={fourthItemIsOpen}
            aria-label={`Bouton pour ${fourthItemIsOpen ? 'fermer' : 'ouvrir'} le block ${title_rejects_microfibers}`}
            id="csr-block-item-4"
            onClick={() => setFourthItemIsOpen(!fourthItemIsOpen)}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="calc(100% - 32px)"
              fontSize="16px"
              lineHeight="110%"
              fontWeight="700"
            >
              <Box fontSize="16px" lineHeight="110%" fontWeight="700">
                {title_rejects_microfibers && title_rejects_microfibers}
              </Box>
              {fourthItemIsOpen && (
                <Box mt="16px" fontSize="14px" lineHeight="18px">
                  {rejectsMicrofibers
                    ? product_rejects_microfibers_is_true
                    : product_rejects_microfibers_is_false}
                </Box>
              )}
            </Box>
            <Box width="32px" display="flex" justifyContent="flex-end">
              <Icon name={fourthItemIsOpen ? 'minus' : 'plus'} size={isMobile ? 12 : 16} />
            </Box>
          </StyledButton>
        )}

        {((toxicity === true && product_toxicity_is_true && product_toxicity_is_true !== '') ||
          (toxicity === false &&
            product_toxicity_is_false &&
            product_toxicity_is_false !== '')) && (
          <StyledButton
            type="button"
            data-testid={`csr-block-item-4-btn-to-${fifthItemIsOpen ? 'close' : 'open'}`}
            aria-expanded={fifthItemIsOpen}
            aria-label={`Bouton pour ${fifthItemIsOpen ? 'fermer' : 'ouvrir'} le block ${title_toxicity}`}
            id="csr-block-item-5"
            onClick={() => setFifthItemIsOpen(!fifthItemIsOpen)}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="calc(100% - 32px)"
              fontSize="16px"
              lineHeight="110%"
              fontWeight="700"
            >
              <Box fontSize="16px" lineHeight="110%" fontWeight="700">
                {title_toxicity && title_toxicity}
              </Box>
              {fifthItemIsOpen && (
                <Box mt="16px" fontSize="14px" lineHeight="18px">
                  {toxicity ? product_toxicity_is_true : product_toxicity_is_false}
                </Box>
              )}
            </Box>
            <Box width="32px" display="flex" justifyContent="flex-end">
              <Icon name={fifthItemIsOpen ? 'minus' : 'plus'} size={isMobile ? 12 : 16} />
            </Box>
          </StyledButton>
        )}
        {glimpactSeason && (
          <>
            <StyledButton
              type="button"
              data-testid={`csr-block-item-6-btn-to-${sixthItemIsOpen ? 'close' : 'open'}`}
              aria-expanded={sixthItemIsOpen}
              aria-label={`Bouton pour ${sixthItemIsOpen ? 'fermer' : 'ouvrir'} le block score environnemental`}
              id="csr-block-item-6"
              isOpen={sixthItemIsOpen}
              onClick={() => setSixthItemIsOpen(!sixthItemIsOpen)}
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                fontSize="16px"
                lineHeight="110%"
                fontWeight="700"
              >
                Score environnemental
                <Icon name={sixthItemIsOpen ? 'minus' : 'plus'} size={isMobile ? 12 : 16} />
              </Box>
            </StyledButton>
            {sixthItemIsOpen && (
              <Box
                mt="16px"
                display="flex"
                justifyContent="center"
                height="716px"
                borderBottom={sixthItemIsOpen ? `solid 1px ${colors.LIGHT}` : 'none'}
                css={css`
                  width: calc(100vw - 32px);
                  @media (min-width: ${breakpoints.S}px) {
                    width: unset;
                  }
                `}
              >
                <iframe
                  className="glimpact-iframe"
                  src={`https://data.glimpact.com/traceability/660-${`${productRef}${glimpactSeason}`}?lng=fr&hasHeader=false`}
                  css={css`
                    border: 1px solid rgba(0, 0, 0, 0.06);
                    border-radius: 1rem;
                    height: 700px;
                    width: calc(100vw - 64px);
                    @media (min-width: ${breakpoints.S}px) {
                      width: 400px;
                    }
                  `}
                />
              </Box>
            )}
          </>
        )}
      </div>
    </div>
  );
};
